// A match record as text for a URL, and back. The text is the JSON of the
// record with the save version, in base64 with the URL alphabet. A link from
// older rules has an older version, and the load ignores it.

import { MATCH_SAVE_VERSION } from '../constants';
import { isCasterList } from '../content/casters';
import type { MatchRecord } from './replay';
import { isMatchMode } from './state';

export type VersionedMatch = MatchRecord & { version: number };

export const isVersionedMatch = (value: Partial<VersionedMatch> | null): value is VersionedMatch =>
  value !== null &&
  typeof value === 'object' &&
  value.version === MATCH_SAVE_VERSION &&
  typeof value.seed === 'number' &&
  isMatchMode(value.mode) &&
  (value.casters === null || isCasterList(value.casters)) &&
  (value.opponentCasters == null || isCasterList(value.opponentCasters)) &&
  Array.isArray(value.actions);

export const withVersion = (record: MatchRecord): VersionedMatch => ({
  ...record,
  version: MATCH_SAVE_VERSION,
});

export const encodeMatch = (record: MatchRecord): string =>
  btoa(JSON.stringify(withVersion(record)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/** Null when the text is not a record of the current rules. */
export const decodeMatch = (text: string): MatchRecord | null => {
  try {
    const parsed: unknown = JSON.parse(atob(text.replace(/-/g, '+').replace(/_/g, '/')));
    return isVersionedMatch(parsed as Partial<VersionedMatch> | null)
      ? (parsed as VersionedMatch)
      : null;
  } catch {
    return null;
  }
};
